/* eslint-disable react-native/no-color-literals */
/**
 * Dark-store item-wise fallback ("कुछ नहीं मिला? आइटम-वाइज़ पिकिंग पर जाएँ").
 *
 * Decision 9: each line reports found / not found / damaged WITH a quantity,
 * then photos. Replicated from the CP's own marking screens so a DB sees the
 * interaction they already know:
 *   - qty 1  -> tap one outcome, no number input   (SingleIssueTooltip)
 *   - qty >1 -> "+ क्वांटिटी" pill that becomes a "− N +" stepper per reason,
 *               the reasons sharing one pool capped at the ordered quantity
 *               (MultiQuantityIssues/CustomerOrderItemIssues)
 *   - a photo inline under डैमेज only, via the same IssuesImagesUpload
 *     component (max 4) — images_mandatory_for_samasya asks for none on
 *     नहीं मिला, and neither does this sheet
 * "मिला" is the remainder, never entered directly — exactly as the marking
 * screens treat it.
 *
 * Why damaged matters: it becomes item_damaged -> a DAMAGED_PRODUCT RVP with a
 * scheduled pickup, so the goods go back. not_found becomes ITEM_MISSING, a
 * refund with no pickup. Recording damage as missing strands the stock.
 */
import React, { useMemo } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  Modal,
  StyleSheet,
  TouchableNativeFeedback,
  TouchableOpacity,
  View,
} from "react-native";
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import Text from "@components/CustomText";
import Cross from "@assets/svg/CrossIcon";
import Plus from "@assets/svg/MyDeliveries/Plus";
import Minus from "@assets/svg/MyDeliveries/Minus";
import SkeletonLoaderList from "@components/SkeletonLoaderList";
import colors from "@constants/color";
import { meta } from "@constants/cplp";
import IssuesImagesUpload from "@screens/MyDeliveries/components/IssuesImagesUpload";
import toasts from "@utils/toast";
import {
  changeIssueQuantity,
  getItemMarking,
  ISSUE_REASONS,
  IssueReason,
  markedUnits,
  PickTpItemsState,
  setIssueImages,
  toggleIssueReason,
} from "./pickTpHelpers";

export interface PickTpFallbackItem {
  order_item_id: number;
  order_id: number;
  title: string;
  product_image?: string;
  expected_quantity: number;
}

interface Props {
  items: PickTpFallbackItem[];
  isLoading: boolean;
  isSubmitting: boolean;
  state: PickTpItemsState;
  onStateChange: (next: PickTpItemsState) => void;
  onClose: () => void;
  onSubmit: () => void;
}

// IssuesImagesUpload gates its add-photo button on can_mark_v2; the pick
// fallback is always a pre-OFD marking.
const UPLOAD_INFO = { can_mark_v2: "PRE_OFD" };

const REASON_LABEL: Record<IssueReason, string> = {
  not_found: meta.not_found,
  item_damaged: meta.item_damaged,
};

const PickTpItemsModalContent: React.FC<Props> = ({
  items,
  isLoading,
  isSubmitting,
  state,
  onStateChange,
  onClose,
  onSubmit,
}) => {
  const insets = useSafeAreaInsets();

  const markedCount = useMemo(
    () =>
      items.filter(
        (item) => markedUnits(getItemMarking(state, item.order_item_id)) > 0,
      ).length,
    [items, state],
  );

  // Photos follow the existing marking rules (images_mandatory_for_samasya):
  // mandatory for डैमेज, not asked for नहीं मिला — the same as every other CP
  // screen. postCpMarkCxItemV4 refuses a damaged marking with no image, so this
  // gate only saves the DB a round trip.
  const missingPhotoFor = useMemo(
    () =>
      items.find(
        (item) =>
          getItemMarking(state, item.order_item_id).item_damaged > 0 &&
          (getItemMarking(state, item.order_item_id).images.item_damaged || [])
            .length === 0,
      ) || null,
    [items, state],
  );

  const handleSubmit = () => {
    if (missingPhotoFor) {
      toasts.showShortToast("कृप्या फोटो डालें!");
      return;
    }
    onSubmit();
  };

  const renderStepper = (item: PickTpFallbackItem, reason: IssueReason) => {
    const marking = getItemMarking(state, item.order_item_id);
    const quantity = marking[reason];
    const atCap = markedUnits(marking) >= item.expected_quantity;
    const change = (delta: number) =>
      onStateChange(
        changeIssueQuantity({
          state,
          orderItemId: item.order_item_id,
          reason,
          delta,
          quantity: item.expected_quantity,
        }),
      );

    if (!quantity) {
      return (
        <TouchableNativeFeedback onPress={() => change(1)} disabled={atCap}>
          <View
            style={[styles.addButton, atCap && styles.addButtonDisabled]}
            testID={`pick-tp-item-${item.order_item_id}-${reason}-add`}
          >
            <Plus stroke={atCap ? colors.black02 : "rgba(255, 46, 0, 1)"} />
            <Text variant="body_text_4" style={styles.addText}>
              क्वांटिटी
            </Text>
          </View>
        </TouchableNativeFeedback>
      );
    }
    return (
      <View style={styles.counterContainer}>
        <TouchableNativeFeedback onPress={() => change(-1)}>
          <View
            style={styles.iconButton}
            testID={`pick-tp-item-${item.order_item_id}-${reason}-minus`}
          >
            <Minus />
          </View>
        </TouchableNativeFeedback>
        <Text
          variant="title_18"
          style={styles.counterText}
          testID={`pick-tp-item-${item.order_item_id}-${reason}-qty`}
        >
          {quantity}
        </Text>
        <TouchableNativeFeedback onPress={() => change(1)}>
          <View
            style={styles.iconButton}
            testID={`pick-tp-item-${item.order_item_id}-${reason}-plus`}
          >
            <Plus stroke={atCap ? colors.basketBallOrange : colors.white} />
          </View>
        </TouchableNativeFeedback>
      </View>
    );
  };

  // qty 1: one outcome for the line, chosen by tapping. Tapping the active one
  // again clears it back to "मिला".
  const renderSingleQtyButtons = (item: PickTpFallbackItem) => {
    const marking = getItemMarking(state, item.order_item_id);
    const isFound = markedUnits(marking) === 0;
    return (
      <View style={styles.singleRow}>
        <TouchableOpacity
          style={[styles.singleBtn, isFound && styles.singleBtnFound]}
          testID={`pick-tp-item-${item.order_item_id}-found`}
          onPress={() =>
            onStateChange({
              ...state,
              [item.order_item_id]: {
                not_found: 0,
                item_damaged: 0,
                images: { not_found: [], item_damaged: [] },
              },
            })
          }
        >
          <Text
            variant="caption_2"
            style={isFound ? styles.singleBtnTextActive : styles.singleBtnText}
          >
            {meta.all_found}
          </Text>
        </TouchableOpacity>
        {ISSUE_REASONS.map((reason) => {
          const active = marking[reason] > 0;
          return (
            <TouchableOpacity
              key={reason}
              style={[
                styles.singleBtn,
                active &&
                  (reason === "item_damaged"
                    ? styles.singleBtnDamaged
                    : styles.singleBtnMissing),
              ]}
              testID={`pick-tp-item-${item.order_item_id}-${reason}`}
              onPress={() =>
                onStateChange(
                  toggleIssueReason({
                    state,
                    orderItemId: item.order_item_id,
                    reason,
                  }),
                )
              }
            >
              <Text
                variant="caption_2"
                style={
                  active ? styles.singleBtnTextActive : styles.singleBtnText
                }
              >
                {REASON_LABEL[reason]}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  const renderItem = ({ item }: { item: PickTpFallbackItem }) => {
    const marking = getItemMarking(state, item.order_item_id);
    const pickedQuantity = item.expected_quantity - markedUnits(marking);
    const isSingle = item.expected_quantity === 1;

    return (
      <View style={styles.card} testID={`pick-tp-item-${item.order_item_id}`}>
        <View style={styles.cardHeader}>
          {item.product_image ? (
            <Image
              source={{ uri: item.product_image }}
              style={styles.image}
              resizeMode="contain"
            />
          ) : (
            <View style={styles.image} />
          )}
          <View style={styles.titleBlock}>
            <Text variant="body_text_4" numberOfLines={2}>
              {item.title}
            </Text>
            <Text variant="caption_2" style={styles.subText}>
              {`Order #${item.order_id} · क्वांटिटी ${item.expected_quantity}`}
            </Text>
          </View>
        </View>

        {isSingle ? (
          renderSingleQtyButtons(item)
        ) : (
          <>
            {ISSUE_REASONS.map((reason) => (
              <View key={reason} style={styles.issueRow}>
                <View style={styles.issueHeader}>
                  <Text variant="body_text_4" style={styles.issueText}>
                    {REASON_LABEL[reason]}
                  </Text>
                  {renderStepper(item, reason)}
                </View>
              </View>
            ))}
            <Text variant="caption_2" style={styles.remainderText}>
              {`${meta.all_found}: ${pickedQuantity} / ${item.expected_quantity}`}
            </Text>
          </>
        )}

        {/* Only डैमेज takes a photo, per images_mandatory_for_samasya. */}
        {marking.item_damaged > 0 ? (
          <View style={styles.uploadBlock}>
            <IssuesImagesUpload
              key={`${item.order_item_id}_item_damaged_ImagesUpload`}
              reason="item_damaged"
              imageRequired={true}
              currentImgs={(marking.images.item_damaged || []).map((url) => ({
                url,
              }))}
              setUploadedImage={(
                uploadReason: IssueReason,
                images: { url?: string }[],
              ) =>
                onStateChange(
                  setIssueImages({
                    state,
                    orderItemId: item.order_item_id,
                    reason: uploadReason,
                    urls: (images || [])
                      .map((image) => image?.url)
                      .filter(Boolean),
                  }),
                )
              }
              info={UPLOAD_INFO}
            />
          </View>
        ) : null}
      </View>
    );
  };

  return (
    <View style={styles.sheet}>
      <View style={styles.header}>
        <Text variant="title_16">आइटम-वाइज़ पिक करें</Text>
        <View style={styles.headerRight}>
          <Text variant="caption_2" style={styles.headerCount}>
            {`${markedCount}/${items.length} आइटम मार्क किए`}
          </Text>
          <TouchableOpacity onPress={onClose} testID="pick-tp-items-close">
            <Cross />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.banner}>
        <Text variant="caption_2" style={styles.bannerText}>
          जो आइटम नहीं मिला या डैमेज है, उसकी क्वांटिटी डालें। डैमेज आइटम की
          फोटो ज़रूरी है। बाकी सब मिला हुआ माना जाएगा।
        </Text>
      </View>

      {isLoading ? (
        <SkeletonLoaderList />
      ) : (
        <FlatList
          data={items}
          keyExtractor={(item) => String(item.order_item_id)}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          testID="pick-tp-items-list"
        />
      )}

      <View style={[styles.footer, { paddingBottom: insets.bottom + 10 }]}>
        <TouchableOpacity
          style={styles.cancelBtn}
          onPress={onClose}
          testID="pick-tp-items-cancel-btn"
        >
          <Text variant="body_text_2">कैंसिल</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.submitBtn, isSubmitting && styles.submitBtnDisabled]}
          onPress={handleSubmit}
          disabled={isSubmitting}
          testID="pick-tp-items-submit-btn"
        >
          {isSubmitting ? (
            <ActivityIndicator color={colors.white} />
          ) : (
            <Text variant="body_text_2" style={styles.submitText}>
              सबमिट करें
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

// Nested SafeAreaProvider so useSafeAreaInsets reports the modal's own window
// (the root provider returns bottom: 0 while a translucent modal is open, which
// clips the footer behind the soft-nav bar).
const PickTpItemsModal: React.FC<Props & { visible: boolean }> = ({
  visible,
  ...props
}) => (
  <Modal
    visible={visible}
    animationType="slide"
    transparent
    statusBarTranslucent
    onRequestClose={props.onClose}
  >
    <SafeAreaProvider>
      <View style={styles.backdrop}>
        <PickTpItemsModalContent {...props} />
      </View>
    </SafeAreaProvider>
  </Modal>
);

export default PickTpItemsModal;

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    justifyContent: "flex-end",
  },
  sheet: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: "92%",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  headerRight: { flexDirection: "row", alignItems: "center" },
  headerCount: { color: colors.plain_green, marginRight: 12 },
  banner: {
    backgroundColor: "rgba(255, 184, 0, 0.12)",
    borderColor: "rgba(255, 184, 0, 0.5)",
    borderWidth: 1,
    borderRadius: 8,
    marginHorizontal: 16,
    padding: 10,
  },
  bannerText: { color: "rgba(120, 80, 0, 1)" },
  listContent: { padding: 16, paddingBottom: 24 },
  card: {
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.10)",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  cardHeader: { flexDirection: "row", alignItems: "center" },
  image: {
    width: 48,
    height: 48,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.10)",
    marginRight: 12,
  },
  titleBlock: { flex: 1 },
  subText: { color: "rgba(0, 0, 0, 0.6)", marginTop: 2 },
  issueRow: { marginTop: 12 },
  issueHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  issueText: { color: "rgba(0, 0, 0, 1)" },
  counterContainer: {
    flexDirection: "row",
    backgroundColor: "rgba(255, 46, 0, 1)",
    borderRadius: 8,
    alignItems: "center",
  },
  counterText: { color: colors.white, paddingHorizontal: 3 },
  iconButton: { paddingHorizontal: 13, paddingVertical: 9 },
  addButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(255, 46, 0, 1)",
    paddingHorizontal: 16,
    paddingVertical: 6,
    flexDirection: "row",
    alignItems: "center",
  },
  addButtonDisabled: { borderColor: colors.black02 },
  addText: { color: "rgba(255, 46, 0, 1)", marginLeft: 8 },
  remainderText: { color: "rgba(0, 0, 0, 0.6)", marginTop: 10 },
  singleRow: { flexDirection: "row", marginTop: 12 },
  singleBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.15)",
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: "center",
    marginRight: 8,
  },
  singleBtnFound: {
    backgroundColor: colors.plain_green,
    borderColor: colors.plain_green,
  },
  singleBtnMissing: {
    backgroundColor: "rgba(255, 152, 0, 1)",
    borderColor: "rgba(255, 152, 0, 1)",
  },
  singleBtnDamaged: {
    backgroundColor: "rgba(255, 46, 0, 1)",
    borderColor: "rgba(255, 46, 0, 1)",
  },
  singleBtnText: { color: "rgba(0, 0, 0, 0.7)" },
  singleBtnTextActive: { color: colors.white },
  uploadBlock: { marginTop: 8 },
  footer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingTop: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.black02,
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.black02,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
    marginRight: 12,
  },
  submitBtn: {
    flex: 2,
    backgroundColor: colors.plain_green,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
  },
  submitBtnDisabled: { opacity: 0.6 },
  submitText: { color: colors.white },
});
